'use client';
import { useState, useEffect, useCallback, useMemo } from 'react';
import Header from '@/components/Header';
import { authFetch, useAuth } from '@/components/AuthProvider';
import Link from 'next/link';

// ─────────────────────────────────────────────
// Greeting helpers
// ─────────────────────────────────────────────
const FRASES = ['Cada venta es una oportunidad de superar expectativas.', 'El precio correcto en el momento correcto es todo.', 'Los datos no mienten — tus decisiones tampoco deberían.', 'Hoy es un buen día para vender más que ayer.', 'La consistencia vence al talento cuando el talento no es consistente.', 'Un catálogo optimizado es tu mejor vendedor.', 'El stock que falta es la venta que se pierde.', 'Velocidad + precisión = dominio del marketplace.', 'Cada publicación activa es una oportunidad abierta.', 'El margen no miente — cuídalo.'];

function getGreeting(name) {
  const h = new Date().getHours();
  const saludo = h < 12 ? 'Buenos días' : h < 19 ? 'Buenas tardes' : 'Buenas noches';
  const first = (name || '').split(' ')[0] || '';
  return first ? `${saludo}, ${first}` : saludo;
}

function getDailyFrase() {
  const day = new Date().getDate() + new Date().getMonth() * 31;
  return FRASES[day % FRASES.length];
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
function fmtARS(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);
}

function fmtPct(n) {
  if (n == null) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
}

const TYPE_LABELS = {
  LIGHTNING: 'Relámpago',
  MARKETPLACE_CAMPAIGN: 'Co-fondeada',
  SMART: 'Smart',
  DEAL: 'Deal',
  PRICE_MATCHING: 'Precio Competitivo',
  SELLER_COUPON_CAMPAIGN: 'Cupón',
};

const TYPE_COLORS = {
  LIGHTNING: '#FF6B00',
  MARKETPLACE_CAMPAIGN: 'var(--accent)',
  SMART: '#00a650',
  DEAL: 'var(--yellow)',
};

const PROMO_TYPE_LABELS = {
  LIGHTNING: 'Relámpago',
  MARKETPLACE_CAMPAIGN: 'Co-fondeada',
  SMART: 'Smart',
  DEAL: 'Deal',
  DOD: 'Oferta del Día',
  SELLER_CAMPAIGN: 'Campaña vendedor',
  PRICE_DISCOUNT: 'Descuento individual',
  PRE_NEGOTIATED: 'Pre-acordado',
  UNHEALTHY_STOCK: 'Liquidación Full',
  SELLER_COUPON_CAMPAIGN: 'Cupón',
  VOLUME: 'Por volumen',
  PRICE_MATCHING: 'Precio competitivo',
};

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────
function KPICard({ label, value, sub, color, href, loading, onClick }) {
  const inner = (
    <div
      onClick={onClick}
      className="rounded-xl p-5 flex flex-col gap-1.5 transition-all h-full"
      style={{
        background: 'var(--bg-2)',
        border: '1px solid var(--border)',
        cursor: href || onClick ? 'pointer' : 'default',
        minHeight: '100px',
      }}
      onMouseEnter={(e) => { if (href) e.currentTarget.style.borderColor = 'var(--accent)'; }}
      onMouseLeave={(e) => { if (href) e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-2)' }}>{label}</span>
      {loading ? (
        <div className="h-8 w-16 rounded" style={{ background: 'var(--bg-elevated)', animation: 'pulse 1.5s ease-in-out infinite' }} />
      ) : (
        <span className="text-[28px] font-bold font-mono leading-none" style={{ color: color || 'var(--text-0)' }}>
          {value ?? '—'}
        </span>
      )}
      {sub && <span className="text-[11px]" style={{ color: 'var(--text-2)' }}>{sub}</span>}
    </div>
  );

  if (href) return <Link href={href} className="no-underline h-full block">{inner}</Link>;
  return <div className="h-full">{inner}</div>;
}

function AlertaItem({ item, tipo }) {
  return (
    <a
      href={item.permalink || `https://articulo.mercadolibre.com.ar/${item.mlid?.replace(/^(MLA)(\d+)/, '$1-$2')}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 px-4 py-3 transition-colors no-underline"
      style={{ borderBottom: '1px solid var(--border)' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = '')}
    >
      {item.thumbnail && (
        <img src={item.thumbnail} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
          style={{ border: '1px solid var(--border)' }} />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-medium truncate" style={{ color: 'var(--text-0)' }}>
          {item.title || item.mlid}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-[10.5px] flex-wrap" style={{ color: 'var(--text-2)' }}>
          {item.sku && <span className="font-mono">SKU {item.sku}</span>}
          {item.mlid && <span className="font-mono" style={{ color: 'var(--accent)' }}>{item.mlid}</span>}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        {tipo === 'bajo_margen' && (
          <>
            <div className="text-[11px] font-mono font-semibold" style={{ color: 'var(--text-0)' }}>
              {fmtARS(item.precio)}
            </div>
            <div
              className="text-[10.5px] font-semibold px-[5px] py-[1px] rounded-[3px] mt-0.5"
              style={{ background: 'rgba(255,59,48,0.12)', color: 'var(--red)' }}
            >
              {fmtPct(item.margen_actual)} (mín {item.margen_minimo}%)
            </div>
          </>
        )}
        {tipo === 'stock_bajo' && (
          <div className="text-[13px] font-bold font-mono" style={{ color: 'var(--yellow)' }}>
            {item.stock} uds
          </div>
        )}
        {tipo === 'sin_stock' && (
          <div className="text-[11px] font-semibold px-[5px] py-[1px] rounded-[3px]"
            style={{ background: 'rgba(255,59,48,0.12)', color: 'var(--red)' }}>
            Sin stock
          </div>
        )}
      </div>
    </a>
  );
}

function HotSaleBanner() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('mp-hotsale-banner-dismissed') === 'true';
  });
  // if (dismissed || !data?.event || data.enabled === false) return null;

  useEffect(() => {
    if (dismissed) return;
    authFetch('/api/hotsale/summary')
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(() => {});
  }, [dismissed]);

  if (dismissed || !data?.event) return null;
  const ev = data.event;
  const t = data.today || {};
  const bl = data.baseline || {};
  const logistic = data.by_logistic || {};

  if (ev.is_finished) return null;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    localStorage.setItem('mp-hotsale-banner-dismissed', 'true');
    setDismissed(true);
  };

  // ── Upcoming ──
  if (ev.is_upcoming) {
    return (
      <Link href="/hotsale" className="no-underline block mb-5">
        <div className="rounded-xl p-4 flex items-center justify-between cursor-pointer transition-all relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #ff3b30 0%, #ff6b35 40%, #ff9500 100%)',
            border: 'none',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.005)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(255,107,53,0.3)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}>
          {/* Background pattern */}
          <div style={{ position: 'absolute', inset: 0, opacity: 0.08, backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,255,255,0.3) 20px, rgba(255,255,255,0.3) 22px)' }} />
          
          <div className="flex items-center gap-3 relative z-10">
            <span className="text-[24px]" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>🔥</span>
            <div>
              <div className="text-[14px] font-bold" style={{ color: '#fff' }}>
                Hot Sale 2026
              </div>
              <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.85)' }}>
                Arranca en <strong style={{ color: '#fff' }}>{ev.days_until} días</strong> — 
                {new Date(ev.start + 'T12:00').toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })} al {new Date(ev.end + 'T12:00').toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 relative z-10">
            <span className="text-[11px] font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>Ver panel →</span>
            <button onClick={handleDismiss}
              className="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', border: 'none' }}
              title="Ocultar banner">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </Link>
    );
  }

  // ── Active ──
  if (ev.is_active) {
    const fullCount = logistic.FULL?.count || 0;
    const flexCount = logistic.FLEX?.count || 0;
    const totalLogistic = Object.values(logistic).reduce((s, v) => s + (v?.count || 0), 0);

    return (
      <Link href="/hotsale" className="no-underline block mb-5">
        <div className="rounded-xl overflow-hidden cursor-pointer transition-all relative"
          style={{
            background: 'linear-gradient(135deg, #ff3b30 0%, #ff6b35 30%, #ff9500 70%, #ffb340 100%)',
            border: 'none',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.003)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(255,107,53,0.35)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}>
          {/* Background pattern */}
          <div style={{ position: 'absolute', inset: 0, opacity: 0.06, backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,255,255,0.4) 20px, rgba(255,255,255,0.4) 22px)' }} />
          
          {/* Top bar */}
          <div className="px-5 py-3 flex items-center justify-between relative z-10">
            <div className="flex items-center gap-3">
              <span className="text-[22px]" style={{ animation: 'pulse-fire 1.5s ease-in-out infinite', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>🔥</span>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-bold" style={{ color: '#fff' }}>Hot Sale 2026</span>
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', animation: 'pulse-glow 2s ease-in-out infinite' }}>
                    ● EN VIVO
                  </span>
                  {ev.days_left != null && (
                    <span className="text-[10px] font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      {ev.days_left}d restantes
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>Ver detalle →</span>
              <button onClick={handleDismiss}
                className="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer"
                style={{ background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', border: 'none' }}
                title="Ocultar banner">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>

          {/* KPIs row */}
          <div className="px-5 pb-4 grid grid-cols-2 md:grid-cols-5 gap-3 relative z-10">
            {[
              { label: 'Órdenes hoy', value: t.total_orders || 0, delta: bl.orders_delta },
              { label: 'Facturación', value: t.total_revenue ? fmtARS(t.total_revenue) : '—' },
              { label: 'Ticket prom.', value: t.avg_ticket ? fmtARS(t.avg_ticket) : '—' },
              { label: 'FULL', value: fullCount, pct: totalLogistic > 0 ? Math.round(fullCount / totalLogistic * 100) : null },
              { label: 'FLEX', value: flexCount, pct: totalLogistic > 0 ? Math.round(flexCount / totalLogistic * 100) : null },
            ].map((kpi, i) => (
              <div key={i} className="rounded-lg px-3 py-2" style={{ background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(4px)' }}>
                <div className="text-[9px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.6)' }}>{kpi.label}</div>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-[18px] font-bold font-mono" style={{ color: '#fff' }}>
                    {typeof kpi.value === 'number' ? kpi.value.toLocaleString('es-AR') : kpi.value}
                  </span>
                  {kpi.delta != null && (
                    <span className="text-[10px] font-bold font-mono px-1 py-0.5 rounded"
                      style={{
                        background: kpi.delta >= 0 ? 'rgba(0,255,100,0.2)' : 'rgba(255,255,255,0.2)',
                        color: '#fff',
                      }}>
                      {kpi.delta >= 0 ? '▲' : '▼'}{Math.abs(kpi.delta)}%
                    </span>
                  )}
                  {kpi.pct != null && (
                    <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.6)' }}>({kpi.pct}%)</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Link>
    );
  }

  return null;
}

// ─────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────
export default function DashboardPage() {
  const { user } = useAuth();
  const greeting = useMemo(() => getGreeting(user?.displayName || user?.name || user?.username), [user]);
  const frase = useMemo(() => getDailyFrase(), []);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alertaTab, setAlertaTab] = useState('bajo_margen');
  const [refreshing, setRefreshing] = useState(false);
  const [fullSinStock, setFullSinStock] = useState(null);
  const [fullSinStockOpen, setFullSinStockOpen] = useState(false);
  const [fullLoading, setFullLoading] = useState(false);

  const load = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const [res, fullRes] = await Promise.all([
        authFetch(`/api/dashboard${force ? '?refresh=1' : ''}`),
        authFetch('/api/stock/full-sin-stock'),
      ]);
      if (!res.ok) throw new Error('Error al cargar dashboard');
      const d = await res.json();
      setData(d);
      if (fullRes.ok) {
        const fd = await fullRes.json();
        setFullSinStock(fd);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pub = data?.publicaciones;
  const prod = data?.productos;
  const alertas = data?.alertas;
  const promos = data?.promociones;

  const alertaActual = alertas?.[alertaTab] || [];
  const alertaTabs = [
    { key: 'bajo_margen', label: 'Bajo margen', count: alertas?.bajo_margen?.length, color: 'var(--red)' },
    { key: 'stock_bajo', label: 'Stock bajo', count: alertas?.stock_bajo?.length, color: 'var(--yellow)' },
    { key: 'sin_stock', label: 'Sin stock', count: alertas?.sin_stock?.length, color: 'var(--text-2)' },
  ];

  return (
    <>
      <Header breadcrumbs={[{ label: 'TeVeCompras', href: '/' }, { label: 'Dashboard' }]} />
      <div className="p-6">

        {/* Title + refresh */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-[22px] font-bold" style={{ color: 'var(--text-0)' }}>{greeting} 👋</h1>
            {data?.generatedAt && (
              <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-2)' }}>
                {data.cached ? '⚡ Cache · ' : ''}
                Actualizado {new Date(data.generatedAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false })}
              </p>
            )}
          </div>
          <div className="hidden md:flex items-center gap-2 max-w-[340px]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ color: 'var(--accent)', flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
            </svg>
            <p className="text-[11.5px] italic leading-snug" style={{ color: 'var(--text-2)' }}>{frase}</p>
          </div>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] font-medium cursor-pointer"
            style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-0)', opacity: refreshing ? 0.6 : 1 }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }}>
              <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
            {refreshing ? 'Actualizando...' : 'Actualizar'}
          </button>
        </div>

        {/* Hot Sale Banner */}
        <HotSaleBanner />

        {error && (
          <div className="rounded-xl p-4 mb-5" style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', color: 'var(--red)' }}>
            {error}
          </div>
        )}

        {/* ── KPIs Publicaciones ── */}
        <div className="mb-2">
          <h2 className="text-[12px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-2)' }}>
            Publicaciones
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6 items-stretch">
          <KPICard label="Productos" value={prod?.total} sub={`${prod?.activos ?? '—'} activos`} href="/productos" loading={loading} />
          <KPICard label="Publicaciones" value={pub?.total} sub={`${pub?.activas ?? '—'} activas`} href="/productos" loading={loading} />
          <KPICard label="Pausadas" value={pub?.pausadas} href="/productos" loading={loading}
            color={pub?.pausadas > 0 ? 'var(--yellow)' : 'var(--text-0)'} />
          <KPICard label="En promo" value={pub?.enPromo} sub="con descuento activo" loading={loading}
            color={pub?.enPromo > 0 ? '#00a650' : 'var(--text-0)'} />
          <KPICard label="Bajo margen" value={pub?.bajoMargen} sub={`< ADO + markup`} loading={loading}
            color={pub?.bajoMargen > 0 ? 'var(--red)' : 'var(--green)'} />
          <KPICard label="Stock bajo" value={pub?.stockBajo} sub={`< 10 unidades`} loading={loading}
            color={pub?.stockBajo > 0 ? 'var(--yellow)' : 'var(--text-0)'} />
          <KPICard label="Sin stock" value={pub?.sinStock} loading={loading}
            color={pub?.sinStock > 0 ? 'var(--red)' : 'var(--text-0)'} />
          <KPICard label="Full sin stock" value={fullSinStock?.total ?? '—'}
            sub={fullSinStock?.total > 0 ? 'activos sin stock Full' : 'sin alertas Full'}
            loading={loading || fullLoading}
            color={fullSinStock?.total > 0 ? 'var(--red)' : 'var(--text-0)'}
            onClick={() => fullSinStock?.total > 0 && setFullSinStockOpen(o => !o)} />
        </div>

        {/* ── Full sin stock — panel colapsable ── */}
        {fullSinStock?.total > 0 && (
          <div className="mb-6 rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,59,48,0.3)', background: 'var(--bg-1)' }}>
            <button
              onClick={() => setFullSinStockOpen(o => !o)}
              className="w-full px-5 py-3.5 flex items-center justify-between cursor-pointer"
              style={{ background: 'rgba(255,59,48,0.06)' }}
            >
              <div className="flex items-center gap-2.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2" strokeLinecap="round">
                  <path d="M20 7l-8-4-8 4m16 0v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4m8-14l-8 4"/>
                </svg>
                <span className="text-[13px] font-semibold" style={{ color: 'var(--red)' }}>
                  {fullSinStock.total} publicación{fullSinStock.total !== 1 ? 'es' : ''} con stock Full en cero
                </span>
                {fullSinStock.importado_at && (
                  <span className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>
                    · snapshot {new Date(fullSinStock.importado_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })}
                  </span>
                )}
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-2)" strokeWidth="2" strokeLinecap="round"
                style={{ transform: fullSinStockOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </button>

            {fullSinStockOpen && (
              <div className="divide-y" style={{ borderTop: '1px solid var(--border)' }}>
                {fullSinStock.items.slice(0, 20).map((item, i) => (
                  <div key={item.inventory_id || i} className="px-5 py-2.5 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium truncate" style={{ color: 'var(--text-0)' }}>
                        {item.titulo || item.inventory_id}
                      </div>
                      <div className="flex items-center gap-2 text-[10.5px] mt-0.5 flex-wrap">
                        {item.sku && <span className="font-mono" style={{ color: 'var(--accent)' }}>SKU {item.sku}</span>}
                        <span className="font-mono" style={{ color: 'var(--text-2)' }}>{item.inventory_id}</span>
                        {item.tiempo_agotar && item.tiempo_agotar !== '-' && (
                          <span className="px-1.5 py-0.5 rounded text-[9.5px] font-semibold"
                            style={{ background: 'rgba(255,59,48,0.1)', color: 'var(--red)' }}>
                            {item.tiempo_agotar}
                          </span>
                        )}
                      </div>
                    </div>
                    {item.ventas_30d > 0 && (
                      <div className="text-right flex-shrink-0">
                        <div className="text-[11px] font-semibold" style={{ color: 'var(--yellow)' }}>{item.ventas_30d} uds/30d</div>
                      </div>
                    )}
                  </div>
                ))}
                {fullSinStock.items.length > 20 && (
                  <div className="px-5 py-2.5 text-[11px] text-center" style={{ color: 'var(--text-2)' }}>
                    y {fullSinStock.items.length - 20} más · <a href="/stock" className="hover:underline" style={{ color: 'var(--accent)' }}>Ver todos en Stock Full</a>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── KPIs Logística ── */}
        {data?.logistica && !data.logistica.error && (
          <div className="mb-6">
            <div className="mb-2">
              <h2 className="text-[12px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-2)' }}>
                Logística · {data.logistica.date ? new Date(data.logistica.date + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Ayer'}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-stretch">
              <KPICard label="Envíos totales" value={data.logistica.total} loading={loading} />
              <KPICard label="Entregados" value={data.logistica.entregados} loading={loading}
                color="#00a650" />
              <KPICard label="No entregados" value={data.logistica.noEntregados} loading={loading}
                color={data.logistica.noEntregados > 0 ? 'var(--red)' : 'var(--text-0)'} />
              <KPICard label="En tránsito" value={data.logistica.enTransito} loading={loading}
                color={data.logistica.enTransito > 0 ? 'var(--yellow)' : 'var(--text-0)'}
                sub="del día anterior" />
              <div className="rounded-xl p-5 flex flex-col gap-1.5 h-full" style={{ background: 'var(--bg-2)', border: `1px solid ${data.logistica.slaPercent >= 95 ? 'rgba(0,166,80,0.3)' : data.logistica.slaPercent >= 80 ? 'rgba(245,158,11,0.3)' : 'rgba(255,59,48,0.3)'}`, minHeight: '100px' }}>
                <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-2)' }}>SLA cumplimiento</span>
                <span className="text-[28px] font-bold font-mono leading-none" style={{ color: data.logistica.slaPercent >= 95 ? '#00a650' : data.logistica.slaPercent >= 80 ? 'var(--yellow)' : 'var(--red)' }}>
                  {data.logistica.slaPercent != null ? `${data.logistica.slaPercent}%` : '—'}
                </span>
                <span className="text-[11px]" style={{ color: 'var(--text-2)' }}>
                  {data.logistica.slaOnTime}/{data.logistica.slaTotal} a tiempo · {data.logistica.slaDelayed} demorados
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── Bottom grid: Alertas + Promociones ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Alertas — ocupa 2/3 */}
          <div className="lg:col-span-2 rounded-xl overflow-hidden" style={{ background: 'var(--bg-2)', border: '1px solid var(--border)' }}>
            <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
              <h2 className="text-[13px] font-semibold" style={{ color: 'var(--text-0)' }}>Alertas activas</h2>
              <Link href="/productos" className="text-[11px] font-medium no-underline" style={{ color: 'var(--accent)' }}>
                Ver productos →
              </Link>
            </div>

            {/* Tabs */}
            <div className="flex" style={{ borderBottom: '1px solid var(--border)' }}>
              {alertaTabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setAlertaTab(t.key)}
                  className="flex-1 px-4 py-2.5 text-[11.5px] font-medium cursor-pointer transition-colors"
                  style={{
                    background: alertaTab === t.key ? 'var(--bg-elevated)' : 'transparent',
                    borderBottom: alertaTab === t.key ? `2px solid ${t.color}` : '2px solid transparent',
                    color: alertaTab === t.key ? t.color : 'var(--text-2)',
                  }}
                >
                  {t.label}
                  {t.count != null && (
                    <span className="ml-1.5 font-mono text-[10px]" style={{ opacity: 0.8 }}>({t.count})</span>
                  )}
                </button>
              ))}
            </div>

            {/* Lista */}
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div style={{ width: 20, height: 20, border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <span className="ml-2 text-[12px]" style={{ color: 'var(--text-2)' }}>Cargando...</span>
              </div>
            ) : alertaActual.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <div className="text-[24px] mb-2">✓</div>
                <div className="text-[12.5px] font-medium" style={{ color: 'var(--text-1)' }}>Sin alertas en esta categoría</div>
              </div>
            ) : (
              <div>
                {alertaActual.map((item) => (
                  <AlertaItem key={item.mlid} item={item} tipo={alertaTab} />
                ))}
                {alertaActual.length >= 10 && (
                  <div className="px-4 py-2.5 text-[11px] text-center" style={{ color: 'var(--text-2)' }}>
                    Mostrando los 10 más críticos
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Promociones — ocupa 1/3 */}
          <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-2)', border: '1px solid var(--border)' }}>
            <div className="px-5 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
              <h2 className="text-[13px] font-semibold" style={{ color: 'var(--text-0)' }}>Promociones activas</h2>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div style={{ width: 20, height: 20, border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              </div>
            ) : promos?.error ? (
              <div className="px-4 py-6 text-[11.5px] text-center" style={{ color: 'var(--text-2)' }}>
                No se pudieron cargar las promociones
              </div>
            ) : (
              <div className="p-4 flex flex-col gap-3">
                {/* Resumen numérico */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg p-3 text-center" style={{ background: 'var(--bg-elevated)' }}>
                    <div className="text-[22px] font-bold font-mono" style={{ color: '#00a650' }}>
                      {promos?.total_activas ?? '—'}
                    </div>
                    <div className="text-[9.5px] uppercase tracking-wider mt-0.5" style={{ color: 'var(--text-2)' }}>Activas</div>
                  </div>
                  <div className="rounded-lg p-3 text-center" style={{ background: 'var(--bg-elevated)' }}>
                    <div className="text-[22px] font-bold font-mono" style={{ color: 'var(--yellow)' }}>
                      {promos?.total_pendientes ?? '—'}
                    </div>
                    <div className="text-[9.5px] uppercase tracking-wider mt-0.5" style={{ color: 'var(--text-2)' }}>Pendientes</div>
                  </div>
                </div>

                {/* Por tipo */}
                {promos?.por_tipo && Object.keys(promos.por_tipo).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--text-2)' }}>Por tipo</div>
                    <div className="flex flex-col gap-1.5">
                      {Object.entries(promos.por_tipo).map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between">
                          <span className="text-[11.5px]" style={{ color: 'var(--text-1)' }}>
                            {PROMO_TYPE_LABELS[type] || type}
                          </span>
                          <span className="text-[12px] font-bold font-mono px-2 py-0.5 rounded-md"
                            style={{ background: 'var(--bg-elevated)', color: TYPE_COLORS[type] || 'var(--text-0)' }}>
                            {count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Destacadas */}
                {promos?.destacadas?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--text-2)' }}>Destacadas</div>
                    <div className="flex flex-col gap-1.5">
                      {promos.destacadas.map((p) => (
                        <div key={p.id} className="rounded-lg px-3 py-2" style={{ background: 'var(--bg-elevated)' }}>
                          <div className="text-[11.5px] font-medium truncate" style={{ color: 'var(--text-0)' }}>{p.name}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[9.5px] font-mono" style={{ color: 'var(--text-2)' }}>{p.id}</span>
                            <span className="text-[9.5px] font-semibold" style={{ color: TYPE_COLORS[p.type] || 'var(--accent)' }}>
                              {TYPE_LABELS[p.type] || p.type}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Links rápidos */}
                <div className="flex flex-col gap-1.5 pt-1" style={{ borderTop: '1px solid var(--border)' }}>
                  {[
                    { href: '/ofertas-relampago', label: '⚡ Ofertas Relámpago' },
                    { href: '/campanias', label: '🤝 Co-fondeadas' },
                    { href: '/cupones', label: '🎟️ Cupones' },
                  ].map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className="text-[11.5px] font-medium px-3 py-2 rounded-lg no-underline transition-colors"
                      style={{ color: 'var(--text-1)', background: 'var(--bg-elevated)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-bg)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-1)'; e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx global>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-fire {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </>
  );
}